/**
 * TelegramAccountsPage (KAN-592)
 *
 * List of Telegram user-accounts of the user's operators with their
 * settings and sending statistics; KAN-601: add / reconnect wizard. Live worker connection state is unknown to
 * the backend, so the page never draws an "online" status and always shows a
 * notice about it. Formatting lives in lib/telegramAccountFormat.
 */
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, Info, Loader2, Plus, Send } from 'lucide-react';
import { PageLayout } from '@/components/layout/PageLayout';
import { ConnectAccountDialog } from '@/components/telegram/ConnectAccountDialog';
import type { ReloginTarget } from '@/components/telegram/ConnectCodeForm';
import { AccountLabelEditor } from '@/components/telegram/AccountLabelEditor';
import { DailyLimitEditor } from '@/components/telegram/DailyLimitEditor';
import { PersonaNameEditor } from '@/components/telegram/PersonaNameEditor';
import { PurchaseCostEditor } from '@/components/telegram/PurchaseCostEditor';
import { PersonalAccountBadge } from '@/components/telegram/PersonalAccountBadge';
import { FloodBadge } from '@/components/telegram/FloodBadge';
import { SellerLink } from '@/components/telegram/SellerLink';
import {
  AccountActions,
  OutreachToggle,
} from '@/components/telegram/AccountActions';
import { AccountProxyButton } from '@/components/telegram/AccountProxyDialog';
import { AccountSendActions } from '@/components/telegram/AccountSendDialog';
import { AccountFilters } from '@/components/telegram/AccountFilters';
import { AccountSortSelect } from '@/components/telegram/AccountSort';
import { AccountsSummary } from '@/components/telegram/AccountsSummary';
import { DecommissionActions } from '@/components/telegram/DecommissionActions';
import { AccountAuthCheck } from '@/components/telegram/AccountAuthCheck';
import { AccountHealth } from '@/components/telegram/AccountHealth';
import { BulkAuthCheck } from '@/components/telegram/BulkAuthCheck';
import { AccountProfile } from '@/components/telegram/AccountProfile';
import { AccountProxyProblem } from '@/components/telegram/AccountProxyProblem';
import { AccountVideoBadges } from '@/components/telegram/AccountVideoBadges';
import { GreetingOperatorsLink } from '@/components/telegram/GreetingOperatorsLink';
import { GreetingVariantsBlock } from '@/components/telegram/GreetingVariantsBlock';
import { LabelPrefixesBlock } from '@/components/telegram/LabelPrefixesBlock';
import { SellersBlock } from '@/components/telegram/SellersBlock';
import {
  OutreachPauseBadge,
  OutreachPauseIcon,
} from '@/components/telegram/OutreachPauseBadge';
import { Button } from '@/components/ui/button';
import { StatusDot } from '@/components/ui/status-dot';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  EMPTY_VALUE,
  connectedViaLabel,
  connectionDetails,
  connectionStatusView,
  formatLastSent,
  hasMultipleOperators,
  isConnectedViaKnown,
  isErrorBeforeLastSend,
  isLimitReached,
  hasKnownConnectionStatus,
  mapLastError,
  outreachLabel,
  settingsEnabledView,
  sourceLabel,
  STATUS_LEGEND,
} from '@/lib/telegramAccountFormat';
import { useHighlightedAccount } from '@/hooks/useHighlightedAccount';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { hasAccountActions } from '@/lib/telegramAccountActions';
import { canChangeProxy } from '@/lib/telegramProxy';
import { canSendFromAccount } from '@/lib/telegramDirectSend';
import {
  filterAccounts,
  frozenAutoDisabledText,
  readAccountSort,
  shownTotal,
  sortAccounts,
  summarizeAccounts,
  writeAccountSort,
  type AccountFilter,
  type AccountSort,
} from '@/lib/telegramAccountList';
import { normalizePhone } from '@/lib/telegramConnect';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type { TelegramAccount } from '@/types/telegramAccount';

const WORKER_NOTICE =
  'Живой статус подключения воркера пока недоступен: показаны настройки и статистика отправок.';
const ERROR_LIMITATION_NOTE =
  'Ошибки рассылки импортированных аккаунтов могут отображаться у основного аккаунта.';
const EMPTY_TEXT =
  'Аккаунтов нет. Нажмите "Добавить аккаунт".';

// KAN-601: статусы меняются без перезагрузки страницы
const REFETCH_INTERVAL_MS = 30_000;

const toRelogin = (a: TelegramAccount): ReloginTarget => ({
  operatorId: a.operator_id,
  accountId: a.account_id,
  phone: normalizePhone(a.phone),
  label: a.label,
  enabled: a.enabled,
  outreach: a.outreach,
  sellerName: a.seller_name,
  sellerUrl: a.seller_url,
  seller: a.seller ?? null,
});

const rowKey = (a: TelegramAccount): string => `${a.operator_id}-${a.account_id}`;

function AccountCell({ account }: { account: TelegramAccount }): JSX.Element {
  const title = account.label || account.username || account.account_id;
  const isPrimary = account.account_id === 'primary';
  return (
    <div className="flex flex-col">
      <AccountProfile account={account} />
      <span className="font-medium">
        {isPrimary ? (
          title
        ) : (
          <AccountLabelEditor
            account={account}
            fallback={account.username || account.account_id}
          />
        )}
        {isPrimary && (
          <span className="ml-2 rounded bg-blue-100 px-1.5 py-0.5 text-xs font-normal text-blue-700">
            основной
          </span>
        )}
      </span>
      {account.phone && (
        <span className="text-xs text-gray-500">{account.phone}</span>
      )}
      <PersonalAccountBadge account={account} />
      <FloodBadge account={account} />
      <SellerLink
        name={account.seller_name}
        url={account.seller_url}
        seller={account.seller}
        editableRating
      />
      {!isPrimary && !account.decommissioned && <PersonaNameEditor account={account} />}
      {!isPrimary && <PurchaseCostEditor account={account} />}
    </div>
  );
}

function ConnectionCell({
  account,
}: {
  account: TelegramAccount;
}): JSX.Element {
  const known = isConnectedViaKnown(account.connected_via);
  const details = connectionDetails(account);
  return (
    <TableCell
      data-testid={`connection-cell-${rowKey(account)}`}
      data-connected-via-known={known ? 'true' : 'false'}
      className="text-sm"
    >
      <div className="flex flex-col">
        <span
          className={
            known ? 'text-gray-700' : 'italic text-gray-500'
          }
        >
          {connectedViaLabel(account.connected_via)}
        </span>
        {details.length > 0 && (
          <span className="text-xs text-gray-500">{details.join(', ')}</span>
        )}
      </div>
    </TableCell>
  );
}

function StatusCell({
  account,
  onReconnect,
  now,
}: {
  account: TelegramAccount;
  onReconnect: () => void;
  now: Date;
}): JSX.Element {
  const view = connectionStatusView(account.status, account.status_until);
  return (
    <TableCell
      data-testid={`status-cell-${rowKey(account)}`}
      data-status-known={view.known ? 'true' : 'false'}
      className="text-sm"
    >
      {view.known ? (
        <StatusDot color={view.color} label={view.label} />
      ) : (
        <span title={view.hint} className="italic text-gray-500">
          {view.label}
        </span>
      )}
      {!account.decommissioned && <AccountProxyProblem account={account} />}
      {!account.decommissioned && <OutreachPauseBadge account={account} now={now} />}
      {account.can_reconnect && !account.decommissioned && (
        <div className="mt-1.5">
          <Button
            variant="outline"
            size="sm"
            className="h-7 px-2 text-xs"
            data-testid={`reconnect-${rowKey(account)}`}
            onClick={onReconnect}
          >
            Переподключить
          </Button>
        </div>
      )}
      {!account.decommissioned && hasAccountActions(account) && (
        <AccountActions account={account} />
      )}
      {canSendFromAccount(account) && <AccountSendActions account={account} />}
      <div className="mt-1.5 text-xs text-gray-500">
        <div data-testid={`proxy-host-${rowKey(account)}`}>
          Прокси: {account.proxy_host || 'нет'}
        </div>
        {canChangeProxy(account) && <AccountProxyButton account={account} />}
      </div>
      <DecommissionActions account={account} />
      {!account.decommissioned && <AccountHealth account={account} />}
      <AccountAuthCheck account={account} />
    </TableCell>
  );
}

function LastErrorCell({
  account,
  now,
}: {
  account: TelegramAccount;
  now: Date;
}): JSX.Element {
  const mapped = mapLastError(account.last_error);
  const stale = isErrorBeforeLastSend(account.last_sent_at, account.last_error_at);
  return (
    <TableCell
      data-testid="last-error-cell"
      data-stale={stale ? 'true' : 'false'}
      className="max-w-[260px] text-sm"
    >
      {mapped ? (
        <div className={stale ? 'flex flex-col opacity-60' : 'flex flex-col'}>
          <span
            className={
              stale
                ? 'font-medium text-gray-500 break-words'
                : 'font-medium text-red-700 break-words'
            }
          >
            {mapped.title}
          </span>
          {mapped.hint && (
            <span className="text-xs text-gray-500">{mapped.hint}</span>
          )}
          <span data-testid="last-error-time" className="text-xs text-gray-400">
            {formatLastSent(account.last_error_at, now)}
          </span>
          {stale && (
            <span className="text-xs italic text-gray-500">
              до последней отправки
            </span>
          )}
        </div>
      ) : (
        <span className="text-gray-400">{EMPTY_VALUE}</span>
      )}
    </TableCell>
  );
}

/** KAN-752: ссылки на страницы операторов для владельца, свои приветствия для остальных. */
function GreetingsSection({ accounts }: { accounts: TelegramAccount[] }): JSX.Element | null {
  const { data: me } = useCurrentUser();
  if (!me?.role) return null;
  if (me.role === 'OWNER') return <GreetingOperatorsLink accounts={accounts} />;
  return me.id ? <GreetingVariantsBlock operatorId={me.id} /> : null;
}

export function TelegramAccountsPage(): JSX.Element {
  useEffect(() => {
    document.title = 'Telegram-аккаунты - SMETA AI';
  }, []);

  const {
    data,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ['telegram-accounts'],
    queryFn: telegramAccountsApi.list,
    refetchInterval: REFETCH_INTERVAL_MS,
  });

  // null: мастер закрыт; {}: новый аккаунт; {relogin}: переподключение
  const [wizard, setWizard] = useState<{ relogin?: ReloginTarget } | null>(null);

  const [filter, setFilter] = useState<AccountFilter>('all');
  // KAN-779: порядок строк, запоминается в localStorage
  const [sort, setSort] = useState<AccountSort>(readAccountSort);
  const changeSort = (next: AccountSort): void => {
    setSort(next);
    writeAccountSort(next);
  };

  const accounts = useMemo(() => data ?? [], [data]);
  const summary = useMemo(() => summarizeAccounts(accounts), [accounts]);
  const visible = useMemo(
    () => filterAccounts(sortAccounts(accounts, sort), filter),
    [accounts, filter, sort]
  );
  const showOperator = hasMultipleOperators(accounts);
  // KAN-645: ?account=<id> from the dashboard / dialog: scroll to it and highlight
  const highlight = useHighlightedAccount<HTMLTableSectionElement>(visible.length > 0);
  const statusKnown = hasKnownConnectionStatus(accounts);
  const now = new Date();

  return (
    <PageLayout
      data-testid="telegram-accounts-page"
      maxWidth="max-w-full"
      title="Telegram-аккаунты"
      subtitle={
        <span className="hidden sm:inline">
          Аккаунты операторов и статистика отправок
        </span>
      }
      titleIcon={
        <div className="w-10 h-10 rounded-xl bg-blue-600 flex items-center justify-center flex-shrink-0">
          <Send className="h-5 w-5 text-white" />
        </div>
      }
      actions={
        <Button
          data-testid="add-account-button"
          onClick={() => setWizard({})}
        >
          <Plus className="mr-2 h-4 w-4" />
          Добавить аккаунт
        </Button>
      }
      breadcrumbs={[
        { label: 'Главная', href: '/' },
        { label: 'Telegram-аккаунты' },
      ]}
    >
      {!statusKnown && (
      <div
        data-testid="worker-status-notice"
        className="mb-4 flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800"
      >
        <Info className="mt-0.5 h-4 w-4 flex-shrink-0" />
        <div className="flex flex-col gap-1">
          <span>{WORKER_NOTICE}</span>
          <span className="text-xs text-blue-700">{ERROR_LIMITATION_NOTE}</span>
        </div>
      </div>
      )}

      {accounts.length > 0 && (
        <p
          data-testid="status-legend"
          className="mb-3 text-xs text-gray-500"
        >
          {STATUS_LEGEND}
        </p>
      )}

      {isLoading && (
        <div
          data-testid="telegram-accounts-loading"
          className="flex items-center justify-center py-12"
        >
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      )}

      {isError && (
        <div
          data-testid="telegram-accounts-error"
          className="flex flex-col items-center justify-center py-12"
        >
          <AlertCircle className="h-12 w-12 text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Ошибка загрузки аккаунтов
          </h3>
          <p className="text-gray-500 mb-4">
            {error instanceof Error ? error.message : 'Произошла ошибка'}
          </p>
          <Button data-testid="retry-button" onClick={() => refetch()}>
            Повторить
          </Button>
        </div>
      )}

      {!isLoading && !isError && accounts.length === 0 && (
        <div
          data-testid="telegram-accounts-empty"
          className="flex flex-col items-center justify-center py-12"
        >
          <Send className="h-12 w-12 text-gray-400 mb-4" />
          <p className="text-gray-500 text-center">{EMPTY_TEXT}</p>
        </div>
      )}

      {!isLoading && !isError && accounts.length > 0 && (
        <div className="mb-3 flex flex-col gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <AccountFilters value={filter} onChange={setFilter} />
            <AccountSortSelect value={sort} onChange={changeSort} />
            <BulkAuthCheck accounts={visible} />
          </div>
          <AccountsSummary
            summary={summary}
            shown={filter === 'all' ? undefined : visible.length}
            shownOf={shownTotal(summary, filter)}
          />
        </div>
      )}

      {!isLoading && !isError && accounts.length > 0 && visible.length === 0 && (
        <div
          data-testid="telegram-accounts-filter-empty"
          className="py-8 text-center text-gray-500"
        >
          Нет аккаунтов под выбранный фильтр
        </div>
      )}

      {!isLoading && !isError && visible.length > 0 && (
        <div className="bg-white rounded-lg border overflow-x-auto">
          <Table aria-label="Telegram accounts table">
            <TableHeader>
              <TableRow>
                {showOperator && <TableHead>Оператор</TableHead>}
                <TableHead>Аккаунт</TableHead>
                <TableHead>Источник</TableHead>
                <TableHead>Подключение</TableHead>
                <TableHead>Видео, 2FA</TableHead>
                <TableHead>В настройках</TableHead>
                <TableHead>Статус соединения</TableHead>
                <TableHead>Рассылка</TableHead>
                <TableHead>Отправлено сегодня</TableHead>
                <TableHead>Последняя отправка</TableHead>
                <TableHead>Последняя ошибка</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody ref={highlight.containerRef}>
              {visible.map((a) => {
                const highlighted = a.account_id === highlight.accountId;
                const limited = isLimitReached(a.sent_today, a.daily_limit);
                const frozenText = frozenAutoDisabledText(a);
                return (
                  <TableRow
                    key={rowKey(a)}
                    data-testid={`telegram-account-row-${rowKey(a)}`}
                    data-highlighted={highlighted ? 'true' : undefined}
                    className={highlighted ? 'bg-yellow-50 ring-2 ring-inset ring-yellow-400' : undefined}
                  >
                    {showOperator && (
                      <TableCell className="text-sm text-gray-700">
                        {a.operator_email}
                      </TableCell>
                    )}
                    <TableCell>
                      <AccountCell account={a} />
                    </TableCell>
                    <TableCell className="text-sm text-gray-700">
                      {sourceLabel(a.source)}
                    </TableCell>
                    <ConnectionCell account={a} />
                    <TableCell>
                      <AccountVideoBadges account={a} />
                    </TableCell>
                    <TableCell>
                      {a.decommissioned ? (
                        <span className="rounded bg-gray-200 px-1.5 py-0.5 text-xs font-medium text-gray-700">
                          Списан
                        </span>
                      ) : (
                        <StatusDot
                          color={settingsEnabledView(a.enabled).color}
                          label={settingsEnabledView(a.enabled).label}
                        />
                      )}
                      {frozenText && (
                        <div className="mt-1 text-xs text-red-700">{frozenText}</div>
                      )}
                    </TableCell>
                    <StatusCell
                      account={a}
                      onReconnect={() => setWizard({ relogin: toRelogin(a) })}
                      now={now}
                    />
                    <TableCell className="text-sm text-gray-700">
                      {outreachLabel(a.outreach)}
                      {!a.decommissioned && hasAccountActions(a) && <OutreachToggle account={a} />}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                      {a.account_id !== 'primary' && !a.decommissioned ? (
                        <DailyLimitEditor account={a} />
                      ) : (
                      <span
                        data-testid={`sent-today-${rowKey(a)}`}
                        data-limit-reached={limited ? 'true' : 'false'}
                        className={
                          limited
                            ? 'rounded bg-red-100 px-2 py-0.5 text-sm font-semibold text-red-700'
                            : 'text-sm text-gray-700'
                        }
                      >
                        {a.sent_today} / {a.daily_limit}
                      </span>
                      )}
                      {!a.decommissioned && <OutreachPauseIcon account={a} now={now} />}
                      </div>
                    </TableCell>
                    <TableCell
                      data-testid="last-sent-cell"
                      className="text-sm text-gray-700"
                    >
                      {formatLastSent(a.last_sent_at, now)}
                    </TableCell>
                    <LastErrorCell account={a} now={now} />
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {statusKnown && accounts.length > 0 && (
        <p
          data-testid="error-limitation-note"
          className="mt-2 text-xs text-gray-500"
        >
          {ERROR_LIMITATION_NOTE}
        </p>
      )}
      {/* KAN-752: владелец настраивает A/B первых сообщений на странице
          оператора (ссылки), не-владелец - свои варианты прямо здесь */}
      <GreetingsSection accounts={accounts} />
      {/* KAN-670: справочник продавцов аккаунтов */}
      <SellersBlock />
      {/* KAN-722: префиксы меток аккаунтов */}
      <LabelPrefixesBlock />
      <ConnectAccountDialog
        open={wizard !== null}
        onOpenChange={(open) => !open && setWizard(null)}
        relogin={wizard?.relogin}
      />
    </PageLayout>
  );
}

export default TelegramAccountsPage;
